"use client";

// import { WhatsApp } from "@mui/icons-material";
import {
  Container,
  Typography,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import Link from "next/link";
import { useState } from "react";
import * as yup from "yup";
import PhoneIcon from "@mui/icons-material/Phone";
import MailIcon from "@mui/icons-material/Mail";
import PlaceIcon from "@mui/icons-material/Place";
import LanguageIcon from "@mui/icons-material/Language";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import { useSnackbar } from "notistack";
import { useCookies } from "react-cookie";
import { useRouter } from "next/navigation";
import { enquiryEmail, EnquiryEmail, EnquiryTypes } from "@/services/emailServiceV2";

interface IEnquire {
  name: string;
  email: string;
  contactNumber: string;
  messageType: string;
  message: string;
}

const inquiryTypes = [
  {
    label: "Common",
    value: "common",
  },
  {
    label: "Flight And Hotel",
    value: "flightAndHotel",
  },
  {
    label: "Flight",
    value: "flight",
  },
  {
    label: "Hotel",
    value: "hotel",
  },
  {
    label: "Deal",
    value: "deal",
  },
  {
    label: "Offer",
    value: "offer",
  },
  {
    label: "Promotion",
    value: "promotion",
  },
  {
    label: "Flash Deals",
    value: "flashDeals",
  },
  {
    label: "Social Offer",
    value: "socialOffer",
  },
];

export const ContactUs: React.FC = () => {
  const EnquireSchema = yup.object().shape({
    name: yup.string().required(),
    email: yup.string().email().required(),
    contactNumber: yup
      .string()
      .required()
      .matches(
        /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/,
        "Phone number is not valid",
      ),
    messageType: yup.string().required(),
    message: yup.string().required(),
  });
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IEnquire>({ resolver: yupResolver(EnquireSchema) });

  const router = useRouter();

  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setCookie] = useCookies(["inquiryType"]);

  const submitEnquire = handleSubmit(async (data: IEnquire) => {
    try {
      setLoading(true);

      const formData: EnquiryEmail = {
        enquiryTypes: [EnquiryTypes[data.messageType as keyof typeof EnquiryTypes] ?? 1],
        sendEmail: true,
        holderDetail: {
          email: data.email,
          phone: data.contactNumber,
          name: data.name,
        },
        message: `${data.message} (this is ${data.messageType} message)`,
      };
      /*
      {
        siteCode: "whv",
        email: data?.email,
        isSaveEnquiry: true,
        data: {
          name: data.name,
          email: data.email,
          phone: data.contactNumber,
          messageType: data.messageType,
          message: data.message,
        },
      }
*/
      await enquiryEmail(formData);

      setCookie("inquiryType", "common", {
        expires: new Date(Date.now() + 60 * 3000),
        path: "/",
      });
      reset();
      router.push("/thank-you");
    } catch (error) {
      enqueueSnackbar("Something went wrong. Please try again in a while", {
        variant: "error",
      });
      console.error(error);
    } finally {
      setLoading(false);
    }
  });

  return (
    <Container>
      <Grid container spacing={2} sx={{ textAlign: "center", mt: 3, mb: 6 }}>
        <Grid
          size={{ xs: 12, sm: 6, md: 6, lg: 3 }}
          sx={{
            order: { xs: 2, md: 3 },
            backgroundColor: "background.paper",
            boxShadow: "0px 1px 10px rgba(0,0,0,0.1)",
            borderRadius: "12px",
            padding: "1rem",
            transition: "all 0.3s ease",
          }}
        >
          <Typography
            variant="h5"
            component="h2"
            color="primary"
            sx={{
              fontSize: { xs: "1.6rem", sm: "1.8rem" },
              fontWeight: 700,
              mb: { xs: "1rem", sm: "1.4rem" },
              textAlign: "start",
            }}
          >
            Opening Hours
          </Typography>

          <Grid container spacing={2} sx={{ textAlign: "center" }}>
            <Grid
              size={{ xs: 4, sm: 6, md: 4 }}
              sx={{
                textAlign: "left",
                fontWeight: 600,
                fontSize: 14,
              }}
            >
              <Typography sx={{ mb: "0.4rem" }}>Monday</Typography>
              <Typography sx={{ mb: "0.4rem" }}>Tuesday</Typography>
              <Typography sx={{ mb: "0.4rem" }}>Wednesday</Typography>
              <Typography sx={{ mb: "0.4rem" }}>Thursday</Typography>
              <Typography sx={{ mb: "0.4rem" }}>Friday</Typography>
              <Typography sx={{ mb: "0.4rem" }}>Saturday</Typography>
              <Typography sx={{ mb: "0.4rem" }}>Sunday</Typography>
            </Grid>

            <Grid
              size={{ xs: 8, sm: 6, md: 8 }}
              sx={{
                textAlign: "right",
                fontSize: 14,
              }}
            >
              <Typography sx={{ mb: "0.4rem" }}>9.00am - 9.00pm</Typography>
              <Typography sx={{ mb: "0.4rem" }}>9.00am - 9.00pm</Typography>
              <Typography sx={{ mb: "0.4rem" }}>9.00am - 9.00pm</Typography>
              <Typography sx={{ mb: "0.4rem" }}>9.00am - 9.00pm</Typography>
              <Typography sx={{ mb: "0.4rem" }}>9.00am - 9.00pm</Typography>
              <Typography sx={{ mb: "0.4rem" }}>11.00am - 8.00pm</Typography>
              <Typography sx={{ mb: "0.4rem" }}>We are closed</Typography>
            </Grid>
          </Grid>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              mt: "8px",
              p: "16px 0px",
              borderTop: "1px solid rgba(0,0,0,0.1)",
            }}
          >
            <Box sx={{ fontSize: "12px" }}>For Emergency Contact (Only on Sundays)</Box>
            {/* <Box
              href="https://api.whatsapp.com/send?phone=447404616090&text=Hi,%20World%20Holiday%20Vibes"
              component="a"
              target="_blank"
              rel="noreferrer nofollow"
              sx={{
                color: "primary.main",
                textDecoration: "none",
                fontWeight: 500,
                verticalAlign: "center",
                display: "flex",
                alignItems: "center",
                fontSize: "15px",
              }}
            >
              <WhatsApp sx={{ width: "20px", mr: "5px" }} />
              074 0461 6090
            </Box> */}
          </Box>
        </Grid>

        <Grid
          size={{
            xs: 12,

            md: 6,
            lg: 6,
          }}
          sx={{
            order: { xs: 1, md: 3 },
            backgroundColor: "background.paper",
            boxShadow: "0px 1px 10px rgba(0,0,0,0.1)",
            borderRadius: "12px",
            padding: "1rem",
            transition: "all 0.3s ease",
          }}
        >
          <Typography
            variant="h5"
            component="h2"
            color="primary"
            sx={{
              fontSize: { xs: "1.6rem", sm: "1.8rem" },
              fontWeight: 700,
              mb: { xs: "1rem", sm: "1.4rem" },
              textAlign: "start",
            }}
          >
            World Cruise Vibes
          </Typography>

          <Typography
            sx={{
              justifyContent: "center",
              textAlign: "left",
              fontSize: { xs: "0.9rem", sm: "1rem" },
              lineHeight: "28px",
            }}
          >
            Got a query about your next holiday? Call us on{" "}
            <Box
              component="a"
              href={"tel:+442081253546"}
              sx={{
                // color: darkMode ? '#fff' : '#222',
                textDecoration: "none",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              {"020 8125 3546"}{" "}
            </Box>
            and speak to our travel experts. You can also simply fill the enquiry form below, and
            our customer service agents will get back to you shortly.
          </Typography>

          <Typography
            sx={{
              justifyContent: "left",
              textAlign: "left",
              mt: "1rem",
              fontSize: { xs: "0.9rem", sm: "1rem" },
              lineHeight: "28px",
            }}
          >
            All holidays booked with us are financially protected accompanied with extra benefits
            such as payment plans, flexible modifications, and a crew of experienced travel
            consultants at your service. That’s right, we took your pulse! So, embark on your next
            holiday in style with World Cruise Vibes.
          </Typography>
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 4,
            lg: 3,
          }}
          sx={{
            order: { xs: 3, md: 3 },
            backgroundColor: "background.paper",
            boxShadow: "0px 1px 10px rgba(0,0,0,0.1)",
            borderRadius: "12px",
            padding: { xs: "1rem", md: "1.5rem" },
            wordBreak: "break-word",
            "& a": {
              color: "inherit",
            },
          }}
        >
          <Typography
            variant="h5"
            component="h2"
            color="primary"
            sx={{
              fontSize: { xs: "1.6rem", sm: "1.8rem" },
              fontWeight: 700,
              mb: { xs: "1rem", sm: "1.4rem" },
              textAlign: "start",
            }}
          >
            Contact Us
          </Typography>

          {
            <Box
              component="a"
              href={``}
              sx={{
                display: "grid",
                gridTemplateColumns: "40px auto",
                color: "#222",
                textDecoration: "none",
              }}
            >
              <PhoneIcon />
              <Box
                sx={{
                  ml: "0px",
                  textAlign: "left",
                  mb: "0.8rem",
                }}
              >
                020 8125 3546
              </Box>
            </Box>
          }

          {/* {!isPPC && (
            <Box
              component="a"
              href="tel:+442081253526"
              sx={{
                display: 'inherit',
                gridTemplateColumns: '40px auto',
                color: darkMode ? '#fff' : '#222',
                textDecoration: 'none',
              }}
            >
              <Box
                sx={{
                  ml: '40px',
                  textAlign: 'left',
                  mb: '0.8rem',
                }}
              >
                020 8125 3526
              </Box>
            </Box>
          )} */}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "40px auto",
              mb: "1rem",
              textDecoration: "none",
            }}
          >
            <MailIcon />
            <Box
              component="a"
              href="mailto:info@worldcruisevibes.com"
              sx={{
                textAlign: "left",
                textDecoration: "none",
              }}
            >
              info@worldcruisevibes.com
            </Box>
          </Box>

          <Box
            component="div"
            sx={{
              display: "grid",
              gridTemplateColumns: "40px auto",
              mb: "1rem",
              textDecoration: "none",
            }}
          >
            <PlaceIcon />
            <Box sx={{ textAlign: "left" }}>14 Cricklewood Ln, London NW2 1EX, United Kingdom.</Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "40px auto",
              mb: "0.5rem",
              textDecoration: "none",
              justifyContent: "start",
            }}
          >
            <LanguageIcon />
            <Link href="/" passHref>
              <Box
                component="a"
                sx={{
                  textDecoration: "none",
                }}
              >
                www.worldcruisevibes.com
              </Box>
            </Link>
          </Box>
        </Grid>
      </Grid>

      <Typography
        variant="h4"
        component="h2"
        color="primary"
        sx={{
          textAlign: "center",
          marginTop: "2rem",
          fontWeight: "600",
        }}
      >
        Send us a Message
      </Typography>
      <form autoComplete="off">
        <Grid container spacing={2} sx={{ marginTop: "1rem" }}>
          <Grid
            size={{
              xs: 12,

              sm: 6,
            }}
          >
            <TextField
              id="outlined-basic"
              label="Name"
              variant="outlined"
              error={errors?.name ? true : false}
              helperText={errors?.name?.message}
              {...register("name")}
              sx={{ width: "100%" }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,

              sm: 6,
            }}
          >
            <TextField
              id="outlined-basic"
              label="Email"
              error={errors?.email ? true : false}
              helperText={errors?.email?.message}
              {...register("email")}
              variant="outlined"
              sx={{ width: "100%" }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,

              sm: 6,
            }}
          >
            <TextField
              id="outlined-basic"
              label="Contact Number"
              variant="outlined"
              error={errors?.contactNumber ? true : false}
              helperText={errors?.contactNumber?.message}
              {...register("contactNumber")}
              sx={{ width: "100%" }}
            />
          </Grid>

          <Grid
            size={{
              xs: 12,

              sm: 6,
            }}
          >
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label" error={errors?.messageType ? true : false}>
                Message Type
              </InputLabel>
              <Select
                {...register("messageType")}
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                label="Message Type"
                error={errors?.messageType ? true : false}
                variant="outlined"
              >
                {inquiryTypes.map((i) => (
                  <MenuItem key={i.value} value={i.value}>
                    {i.label}
                  </MenuItem>
                ))}
              </Select>
              {errors?.messageType?.message && (
                <FormHelperText error>{errors?.messageType?.message}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              id="outlined-basic"
              label="Message"
              variant="outlined"
              multiline
              sx={{ width: "100%" }}
              error={errors?.message ? true : false}
              helperText={errors?.message?.message}
              {...register("message")}
            />
          </Grid>
        </Grid>
      </form>
      <div></div>

      <Grid
        size={{ xs: 12 }}
        sx={{
          textAlign: { xs: "left", md: "right" },
          my: "1rem",
        }}
      >
        <LoadingButton
          variant="contained"
          disableElevation
          sx={{
            background: "primary",
            color: "white",
            "& .MuiCircularProgress-svg": {
              color: "white",
            },
          }}
          onClick={submitEnquire}
          disabled={loading}
          loading={loading}
        >
          send message
        </LoadingButton>
      </Grid>

      <Box
        sx={{ height: "24rem", mt: "1rem", borderRadius: "8px" }}
        component={"iframe"}
        title="contact us"
        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2478.744484364211!2d0.0283798!3d51.5912462!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48760d2df2383449%3A0x114c6b59503a1f0a!2sWorld%20Holiday%20Vibes!5e0!3m2!1sen!2slk!4v1685706896024!5m2!1sen!2slk"
        width="100%"
        height="400"
        frameBorder="0"
      ></Box>
    </Container>
  );
};
